import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiClock,
  FiMessageCircle,
  FiTrash2,
  FiTrendingUp,
  FiUserPlus,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiPhone,
} from "react-icons/fi";
import csrOutreachService from "../../api/services/csrOutreachService";
import CsrCustomerChat from "./CsrCustomerChat";
import CsrCallHistoryModal from "./CsrCallHistoryModal";
import CsrPremiumProofModal from "./CsrPremiumProofModal";
import { toDatetimeLocalValue } from "../../utils/callDuration";
import { CallRecordsTable } from "../../components/CallRecordsTable";
import ConfirmWarningModal from "../../components/ConfirmWarningModal";
import { notifyDailyTargetsChanged } from "../../utils/dailyTargetsEvents";
import { TableSkeleton } from "../../components/common/skeletons";

const THEME = { primary: "#FB565F" };
const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 outline-none";

const ALLOWED = ["Admin", "Developer", "CSR", "CSR Team Lead"];
const MAX_BATCH = 100;

function premiumStatusBadge(customer) {
  const status = customer?.premiumRequest?.status;
  if (customer?.buyPremium || status === "approved") {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-800">
        Approved
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-800">
        Pending TL
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-rose-100 text-rose-800">
        Rejected
      </span>
    );
  }
  return null;
}

function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-xl text-sm font-semibold ${
        active ? "text-white shadow" : "bg-gray-100 text-gray-600"
      }`}
      style={active ? { backgroundColor: THEME.primary } : {}}
    >
      {children}
    </button>
  );
}

export default function CsrOutreachPage() {
  const userRole = localStorage.getItem("adminUserRole") || "Admin";
  const isAdmin = userRole === "Admin" || userRole === "Developer";
  const isDeveloper = userRole === "Developer";
  const isLead = userRole === "CSR Team Lead";
  const isCsr = userRole === "CSR" || isLead;
  const canManageCsrCustomers = isCsr;

  const [tab, setTab] = useState(isAdmin ? "performance" : "mine");
  const [stages, setStages] = useState([]);
  const [pool, setPool] = useState([]);
  const [mine, setMine] = useState([]);
  const [team, setTeam] = useState([]);
  const [teams, setTeams] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [callRecords, setCallRecords] = useState([]);
  const [quota, setQuota] = useState(null);
  const [period, setPeriod] = useState("daily");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [chatCustomer, setChatCustomer] = useState(null);
  const [callModal, setCallModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [loggingCall, setLoggingCall] = useState(false);
  const [deletingCallId, setDeletingCallId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [dataVersion, setDataVersion] = useState(0);
  const [premiumModalCustomer, setPremiumModalCustomer] = useState(null);
  const [premiumRequests, setPremiumRequests] = useState([]);
  const [approvalModal, setApprovalModal] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [showAddByPhone, setShowAddByPhone] = useState(false);
  const [phoneLead, setPhoneLead] = useState("");
  const [phoneLookup, setPhoneLookup] = useState(null);
  const [phoneLookupError, setPhoneLookupError] = useState("");
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneAssigning, setPhoneAssigning] = useState(false);
  const [teamAgents, setTeamAgents] = useState([]);
  const [reassigningId, setReassigningId] = useState(null);
  const [unassigningId, setUnassigningId] = useState(null);
  const [targetDate, setTargetDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [targetRows, setTargetRows] = useState([]);
  const [targetForm, setTargetForm] = useState({
    agent_id: "",
    target_mode: "calls_and_premium",
    calls_target: "20",
    premium_target: "5",
    apply_to_all: false,
    include_self: false,
  });
  const [savingTargets, setSavingTargets] = useState(false);

  const remaining = quota?.remainingSlots ?? MAX_BATCH;
  const canVerifyPremium = isLead || isAdmin;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const stagesRes = await csrOutreachService.getStages();
      setStages(stagesRes.stages || []);

      if (tab === "assign" && isCsr) {
        const [poolRes, quotaRes] = await Promise.all([
          csrOutreachService.getPool({ search: debouncedSearch }),
          csrOutreachService.getQuota(),
        ]);
        setPool(poolRes.customers || []);
        setQuota(quotaRes);
      } else if (tab === "mine" && isCsr) {
        const data = await csrOutreachService.getMine({ search: debouncedSearch });
        setMine(data.customers || []);
        setQuota(data.quota || null);
      } else if (tab === "team" && (isLead || isAdmin)) {
        const data = await csrOutreachService.getTeam({ search: debouncedSearch });
        setTeam(data.customers || []);
      } else if (tab === "targets" && (isLead || isAdmin)) {
        const data = await csrOutreachService.getTargets({ date: targetDate });
        setTargetRows(data.agents || []);
        if (!targetForm.agent_id && data.agents?.length) {
          setTargetForm((f) => ({ ...f, agent_id: String(data.agents[0].agentId) }));
        }
      } else if (tab === "performance") {
        const data = await csrOutreachService.getPerformance({ period });
        setPerformance(data.performance || []);
        setCallRecords(data.callRecords || []);
      } else if (tab === "teams" && (isLead || isAdmin)) {
        const data = await csrOutreachService.getTeams();
        setTeams(data.teams || []);
      }
    } catch {
      toast.error("Failed to load CSR outreach data");
    } finally {
      setLoading(false);
    }
  }, [tab, debouncedSearch, isCsr, isLead, isAdmin, period, targetDate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!(isLead || isAdmin)) return;
    csrOutreachService
      .getAccess()
      .then((res) => setTeamAgents(res.access?.agents || []))
      .catch(() => setTeamAgents([]));
  }, [isLead, isAdmin]);

  useEffect(() => {
    const phone = phoneLead.trim();
    if (!showAddByPhone || phone.replace(/\D/g, "").length < 7) {
      setPhoneLookup(null);
      setPhoneLookupError("");
      return;
    }
    let cancelled = false;
    setPhoneChecking(true);
    const t = setTimeout(async () => {
      try {
        const data = await csrOutreachService.lookupPhone(phone);
        if (cancelled) return;
        setPhoneLookup(data);
        setPhoneLookupError("");
      } catch (e) {
        if (cancelled) return;
        setPhoneLookup(null);
        setPhoneLookupError(e?.response?.data?.message || "Phone check failed");
      } finally {
        if (!cancelled) setPhoneChecking(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [phoneLead, showAddByPhone]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [tab, debouncedSearch]);

  if (!ALLOWED.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  const list =
    tab === "assign" ? pool : tab === "mine" ? mine : tab === "team" ? team : [];

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < remaining) next.add(id);
      else toast.error(`Max ${remaining} user(s) in this batch`);
      return next;
    });
  };

  const handleBulkAssign = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setAssigning(true);
    try {
      const res = await csrOutreachService.assign(ids);
      toast.success(res.message || `Assigned ${ids.length}`);
      if (res.quota) setQuota(res.quota);
      setSelectedIds(new Set());
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Assign failed");
      if (e?.response?.data?.quota) setQuota(e.response.data.quota);
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignByPhone = async () => {
    if (!phoneLead.trim()) {
      toast.error("Phone number is required");
      return;
    }
    if (phoneLookupError) {
      toast.error(phoneLookupError);
      return;
    }
    setPhoneAssigning(true);
    try {
      const res = await csrOutreachService.assignByPhone(phoneLead.trim());
      toast.success(res.message || "Lead assigned");
      if (res.quota) setQuota(res.quota);
      setShowAddByPhone(false);
      setPhoneLead("");
      setPhoneLookup(null);
      setPhoneLookupError("");
      setTab("mine");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Assign by phone failed");
      if (e?.response?.data?.quota) setQuota(e.response.data.quota);
    } finally {
      setPhoneAssigning(false);
    }
  };

  const handleRelease = async (assignmentId) => {
    if (!window.confirm("Unassign this customer so they return to the pool?")) return;
    setUnassigningId(assignmentId);
    try {
      await csrOutreachService.releaseAssignment(assignmentId);
      toast.success("Customer unassigned");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Unassign failed");
    } finally {
      setUnassigningId(null);
    }
  };

  const handleReassign = async (assignmentId, agentId) => {
    if (!agentId) return;
    setReassigningId(assignmentId);
    try {
      await csrOutreachService.reassignAssignment(assignmentId, Number(agentId));
      toast.success("Customer shifted to another CSR");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Reassign failed");
    } finally {
      setReassigningId(null);
    }
  };

  const handleStage = async (assignmentId, stageId) => {
    if (!canManageCsrCustomers) return;
    try {
      await csrOutreachService.updateStage(assignmentId, stageId);
      toast.success("Status updated");
      refresh();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handlePremium = async (customer, buy) => {
    if (!canManageCsrCustomers || !customer?.assignmentId) return;
    if (buy) {
      if (customer.isPremium) {
        toast("User is already premium — buy premium is not required");
        return;
      }
      if (customer.premiumRequest?.status === "pending") {
        toast.error("Payment proof already pending team lead review");
        return;
      }
      if (customer.buyPremium) {
        toast("Buy premium already approved");
        return;
      }
      setPremiumModalCustomer(customer);
      return;
    }
    if (customer.premiumRequest?.status === "pending") {
      toast.error("Cannot clear while payment proof is pending review");
      return;
    }
    try {
      await csrOutreachService.updateBuyPremium(customer.assignmentId, false);
      toast.success("Cleared buy premium");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update");
    }
  };

  const handlePremiumApproval = async () => {
    if (!approvalModal?.id || !approvalModal?.status) return;
    setApprovingId(approvalModal.id);
    try {
      await csrOutreachService.approvePremiumRequest(approvalModal.id, {
        status: approvalModal.status,
        notes: approvalModal.notes || undefined,
      });
      toast.success(
        approvalModal.status === "approved"
          ? "Payment approved — premium granted"
          : "Payment proof rejected"
      );
      setApprovalModal(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update approval");
    } finally {
      setApprovingId(null);
    }
  };

  const openLogCallModal = (customer) => {
    if (!canManageCsrCustomers) return;
    const ended = new Date();
    const started = new Date(ended.getTime() - 5 * 60 * 1000);
    setCallModal({
      userId: customer.userId,
      email: customer.email || "",
      firstName: customer.firstName,
      lastName: customer.lastName,
      hours: 0,
      minutes: 5,
      seconds: 0,
      started_at: toDatetimeLocalValue(started),
      notes: "",
    });
  };

  const handleLogMinutes = async () => {
    if (!callModal) return;
    const hours = Number(callModal.hours) || 0;
    const minutes = Number(callModal.minutes) || 0;
    const seconds = Number(callModal.seconds) || 0;
    if (hours + minutes + seconds <= 0) {
      toast.error("Enter call duration (hours, minutes, or seconds)");
      return;
    }
    setLoggingCall(true);
    try {
      await csrOutreachService.logCallMinutes({
        user_id: callModal.userId,
        email: callModal.email,
        hours,
        minutes,
        seconds,
        started_at: callModal.started_at ? new Date(callModal.started_at).toISOString() : undefined,
        notes: callModal.notes || "",
      });
      toast.success("Call record saved");
      notifyDailyTargetsChanged({ team: "csr", action: "call_logged" });
      setCallModal(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to log call");
    } finally {
      setLoggingCall(false);
    }
  };

  const executeDeleteCallRecord = async (call) => {
    if (!call?.id || !isDeveloper) return;
    setDeletingCallId(call.id);
    try {
      await csrOutreachService.deleteCallRecord(call.id);
      toast.success("Call record deleted");
      setConfirmModal(null);
      setDataVersion((v) => v + 1);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to delete call record");
    } finally {
      setDeletingCallId(null);
    }
  };

  const requestDeleteCallRecord = (call) => {
    if (!call?.id || !isDeveloper) return;
    setConfirmModal({
      title: "Delete call record?",
      message: "This will permanently remove this call entry from history and update totals.",
      confirmLabel: "Delete record",
      variant: "danger",
      onConfirm: () => executeDeleteCallRecord(call),
    });
  };

  const executeDeleteCustomer = async (customer) => {
    if (!customer?.assignmentId || !isDeveloper) return;
    setDeletingCallId(`customer-${customer.assignmentId}`);
    try {
      await csrOutreachService.deleteCustomerAssignment(customer.assignmentId);
      toast.success("Customer record and related data removed");
      setConfirmModal(null);
      setDataVersion((v) => v + 1);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to delete customer record");
    } finally {
      setDeletingCallId(null);
    }
  };

  const requestDeleteCustomer = (customer) => {
    if (!customer?.assignmentId || !isDeveloper) return;
    const name =
      [customer.firstName, customer.lastName].filter(Boolean).join(" ").trim() || customer.email;
    setConfirmModal({
      title: `Remove "${name}" from CSR outreach?`,
      message: [
        "This deletes all chat messages, call logs, and releases the assignment.",
        "The user returns to the assign pool.",
      ],
      confirmLabel: "Remove customer",
      variant: "danger",
      onConfirm: () => executeDeleteCustomer(customer),
    });
  };

  const saveCsrTargets = async () => {
    if (!targetForm.apply_to_all && !targetForm.agent_id) {
      toast.error("Select a CSR agent");
      return;
    }
    setSavingTargets(true);
    try {
      await csrOutreachService.saveTargets({
        target_date: targetDate,
        agent_id: Number(targetForm.agent_id) || undefined,
        apply_to_all: targetForm.apply_to_all,
        include_self: targetForm.apply_to_all ? targetForm.include_self : undefined,
        target_mode: targetForm.target_mode,
        calls_target: Number(targetForm.calls_target) || 0,
        premium_target:
          targetForm.target_mode === "calls_only" ? 0 : Number(targetForm.premium_target) || 0,
      });
      toast.success(
        targetForm.apply_to_all
          ? `Targets applied to all CSR agents for ${targetDate}`
          : `Targets saved for ${targetDate}`
      );
      notifyDailyTargetsChanged({ team: "csr", targetDate });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save targets");
    } finally {
      setSavingTargets(false);
    }
  };

  const tabs = [
    ...(isCsr ? [{ id: "assign", label: "Assign Users" }] : []),
    ...(isCsr ? [{ id: "mine", label: "My Users" }] : []),
    ...(isLead || isAdmin ? [{ id: "team", label: "Team View" }] : []),
    ...(isLead || isAdmin ? [{ id: "targets", label: "Daily Targets" }] : []),
    { id: "performance", label: "Performance" },
    ...(isLead || isAdmin ? [{ id: "teams", label: "Teams" }] : []),
  ];

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-4 md:p-6 min-h-[calc(100vh-120px)]">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiUsers style={{ color: THEME.primary }} /> CSR Outreach
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Pick up to {MAX_BATCH} users per batch · message anytime · call only when online · track status &amp; premium
            </p>
          </div>
          {quota && isCsr && (
            <div className="text-right text-sm">
              <p className="font-semibold text-gray-800">
                Active: {quota.activeUnresolved}/{quota.maxBatch}
              </p>
              <p className="text-xs text-gray-500">{quota.remainingSlots} slots left</p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {tabs.map((t) => (
            <TabBtn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
            </TabBtn>
          ))}
        </div>

        {isAdmin && (tab === "team" || tab === "mine") && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
            View only — Admin/Developer accounts can monitor CSR outreach but cannot contact customers, log
            call time, change status, or update buy premium.
          </p>
        )}

        {tab === "assign" && quota && (
          <div
            className={`rounded-lg px-4 py-2 text-sm mb-4 ${
              quota.canAssignMore ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
            }`}
          >
            Batch limit: <strong>{quota.activeUnresolved}/{quota.maxBatch}</strong> ·{" "}
            {quota.remainingSlots} slot(s) available
            {!quota.canAssignMore && (
              <span className="block mt-1">
                Mark users Completed/Resolved to unlock the next {MAX_BATCH}.
              </span>
            )}
          </div>
        )}

        {tab === "assign" && isCsr && (
          <div className="mb-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setPhoneLead("");
                setPhoneLookup(null);
                setPhoneLookupError("");
                setShowAddByPhone(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ backgroundColor: THEME.primary }}
            >
              <FiPhone /> Add lead by phone
            </button>
          </div>
        )}
        {tab === "assign" && quota?.canAssignMore !== false && (
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set(list.slice(0, remaining).map((c) => c.userId)))}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100"
            >
              Select all (up to {remaining})
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100"
            >
              Clear
            </button>
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={handleBulkAssign}
                disabled={assigning}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                <FiUserPlus className="inline mr-1" /> Assign selected ({selectedIds.size})
              </button>
            )}
          </div>
        )}

        {tab === "performance" && (
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setPeriod(period === "daily" ? "monthly" : "daily")}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100"
            >
              {period === "daily" ? "Show monthly" : "Show daily"}
            </button>
          </div>
        )}

        {tab === "teams" && (
          <p className="text-sm text-gray-500 mb-4">
            Manage team lead &amp; members in{" "}
            <Link to="/support-teams" className="text-red-600 font-semibold underline">
              Support Teams
            </Link>
            . Overview below.
          </p>
        )}

        {(tab === "assign" || tab === "mine" || tab === "team") && (
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${inputCls} w-full max-w-md mb-4`}
          />
        )}

        {loading ? (
          <TableSkeleton rows={8} cols={6} />
        ) : tab === "targets" ? (
          <div className="space-y-5">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="font-bold text-gray-900 mb-1">Set CSR daily targets</h2>
              <p className="text-xs text-gray-500 mb-4">
                Targets apply to <strong>CSR agents only</strong> (not Chat Support). Choose calls only, or calls +
                premium.
              </p>
              <div className="flex flex-wrap gap-3 items-end mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div className="min-w-[200px]">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">CSR agent</label>
                  <select
                    value={targetForm.agent_id}
                    disabled={targetForm.apply_to_all}
                    onChange={(e) => setTargetForm((f) => ({ ...f, agent_id: e.target.value }))}
                    className={`${inputCls} w-full`}
                  >
                    {(targetRows.length ? targetRows : teamAgents).map((a) => (
                      <option key={a.agentId || a.id} value={a.agentId || a.id}>
                        {a.isTeamLeadSelf
                          ? `${a.agentName || a.name || a.email} (You)`
                          : a.agentName || a.name || a.email}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
                  <input
                    type="checkbox"
                    checked={targetForm.apply_to_all}
                    onChange={(e) => setTargetForm((f) => ({ ...f, apply_to_all: e.target.checked }))}
                  />
                  Apply to all CSR agents
                </label>
                {targetForm.apply_to_all ? (
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
                    <input
                      type="checkbox"
                      checked={targetForm.include_self}
                      onChange={(e) => setTargetForm((f) => ({ ...f, include_self: e.target.checked }))}
                    />
                    Include my own target
                  </label>
                ) : null}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Target type</label>
                  <select
                    value={targetForm.target_mode}
                    onChange={(e) => setTargetForm((f) => ({ ...f, target_mode: e.target.value }))}
                    className={`${inputCls} w-full`}
                  >
                    <option value="calls_and_premium">Calls + premium</option>
                    <option value="calls_only">Calls only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Calls target</label>
                  <input
                    type="number"
                    min={0}
                    value={targetForm.calls_target}
                    onChange={(e) => setTargetForm((f) => ({ ...f, calls_target: e.target.value }))}
                    className={`${inputCls} w-full`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Premium target</label>
                  <input
                    type="number"
                    min={0}
                    disabled={targetForm.target_mode === "calls_only"}
                    value={targetForm.premium_target}
                    onChange={(e) => setTargetForm((f) => ({ ...f, premium_target: e.target.value }))}
                    className={`${inputCls} w-full ${
                      targetForm.target_mode === "calls_only" ? "bg-gray-50 text-gray-400" : ""
                    }`}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={saveCsrTargets}
                disabled={savingTargets || !(targetRows.length || teamAgents.length)}
                className="px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {savingTargets ? "Saving…" : "Save targets"}
              </button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">CSR</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">Mode</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Calls target</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Calls done</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Premium target</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Premium done</th>
                  </tr>
                </thead>
                <tbody>
                  {targetRows.map((r) => (
                    <tr key={r.agentId} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {r.isTeamLeadSelf
                          ? `${r.agentName || r.agentEmail} (You)`
                          : r.agentName || r.agentEmail}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {r.targets?.targetMode === "calls_only"
                          ? "Calls only"
                          : r.targetsSet
                            ? "Calls + premium"
                            : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">{r.targets?.callsTarget ?? "—"}</td>
                      <td className="px-3 py-2 text-center">{r.actuals?.callsCount ?? 0}</td>
                      <td className="px-3 py-2 text-center">
                        {r.targets?.targetMode === "calls_only"
                          ? "—"
                          : r.targets?.premiumTarget ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-center">{r.actuals?.premiumCount ?? 0}</td>
                    </tr>
                  ))}
                  {!targetRows.length && (
                    <tr>
                      <td colSpan={6} className="px-3 py-12 text-center text-gray-400">
                        No CSR agents found. Assign CSR users to a team first.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === "performance" ? (
          <div className="space-y-5">
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">CSR</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Active batch</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Buy premium</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Calls</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Call min</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Logged min</th>
                  </tr>
                </thead>
                <tbody>
                  {performance.map((r) => (
                    <tr key={r.agentId} className="border-t">
                      <td className="px-3 py-2">
                        <p className="font-medium">{r.name}</p>
                        <p className="text-xs text-gray-400">{r.email}</p>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {r.activeUnresolved ?? 0}/{MAX_BATCH}
                      </td>
                      <td className="px-3 py-2 text-center">{r.buyPremiumCount ?? 0}</td>
                      <td className="px-3 py-2 text-center">{r.calls_count || 0}</td>
                      <td className="px-3 py-2 text-center">
                        {Math.round((r.calls_duration_sec || 0) / 60)}
                      </td>
                      <td className="px-3 py-2 text-center">{Math.round(r.totalCallMinutes || 0)}</td>
                    </tr>
                  ))}
                  {!performance.length && (
                    <tr>
                      <td colSpan={6} className="px-3 py-12 text-center text-gray-400">
                        No performance data yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <section className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-1">Call records</h3>
              <p className="text-xs text-gray-500 mb-3">
                Every manual log and in-app chat call for this period ({period === "daily" ? "today" : "this month"}).
              </p>
              <CallRecordsTable
                records={callRecords}
                showAgent={isLead || isAdmin}
                canDelete={isDeveloper}
                deletingId={deletingCallId}
                onDelete={requestDeleteCallRecord}
                emptyMessage="No call records for this period yet."
              />
            </section>
          </div>
        ) : tab === "teams" ? (
          <div className="space-y-4">
            {teams.map((t) => (
              <div key={t.id} className="rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900">{t.name}</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Lead: {t.teamLeadName || "—"} · {t.members?.length || 0} members
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(t.members || []).map((m) => (
                    <span
                      key={m.id}
                      className="text-xs px-2 py-1 rounded-lg bg-gray-50 border text-gray-700"
                    >
                      {m.firstName} {m.lastName}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {!teams.length && (
              <p className="text-center text-gray-400 py-12">No teams yet — create one in Support Teams</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {tab === "assign" && <th className="px-3 py-2 w-10" />}
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Customer</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Online</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Premium</th>
                  {tab !== "assign" && (
                    <>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Status</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Buy premium</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Call min</th>
                    </>
                  )}
                  {tab === "team" && (
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">CSR</th>
                  )}
                  <th className="px-3 py-2 text-right font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.userId || c.assignmentId} className="border-t">
                    {tab === "assign" && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.userId)}
                          disabled={!selectedIds.has(c.userId) && selectedIds.size >= remaining}
                          onChange={() => toggle(c.userId)}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <p className="font-medium">
                        {c.firstName} {c.lastName}
                      </p>
                      <p className="text-xs text-gray-400">{c.email}</p>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          c.isOnline ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {c.isOnline ? "Online" : "Offline"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          c.isPremium ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {c.isPremium ? "Premium" : "Free"}
                      </span>
                    </td>
                    {tab !== "assign" && (
                      <>
                        <td className="px-3 py-2">
                          {canManageCsrCustomers ? (
                            <select
                              value={stages.find((s) => s.stage_key === c.stageKey)?.id || ""}
                              onChange={(e) => handleStage(c.assignmentId, Number(e.target.value))}
                              className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                            >
                              {stages.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-xs font-semibold text-gray-600">
                              {c.stageName || c.stageKey || "—"}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {canManageCsrCustomers ? (
                            <div className="flex flex-col items-start gap-1">
                              {c.isPremium ? (
                                <span className="text-[11px] font-semibold text-emerald-700">
                                  Already premium — not required
                                </span>
                              ) : (
                                <>
                                  <label className="inline-flex items-center gap-1 text-xs">
                                    <input
                                      type="checkbox"
                                      checked={
                                        !!c.buyPremium || c.premiumRequest?.status === "pending"
                                      }
                                      disabled={
                                        !!c.buyPremium || c.premiumRequest?.status === "pending"
                                      }
                                      onChange={(e) => handlePremium(c, e.target.checked)}
                                    />
                                    Buy premium
                                  </label>
                                  {premiumStatusBadge(c)}
                                  {c.premiumRequest?.status === "rejected" && (
                                    <button
                                      type="button"
                                      className="text-[10px] font-semibold text-blue-700 hover:underline"
                                      onClick={() => setPremiumModalCustomer(c)}
                                    >
                                      Resubmit proof
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <span className="text-xs text-gray-600">
                                {c.isPremium
                                  ? "Already premium"
                                  : c.buyPremium
                                    ? "Yes"
                                    : "No"}
                              </span>
                              {!c.isPremium && premiumStatusBadge(c)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => setHistoryModal(c)}
                            className="text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline"
                            title="View all call records"
                          >
                            {Number(c.callMinutes || 0).toFixed(1)} min
                          </button>
                        </td>
                      </>
                    )}
                    {tab === "team" && (
                      <td className="px-3 py-2 text-xs text-gray-600">{c.agentName || "—"}</td>
                    )}
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        {tab === "assign" ? (
                          <button
                            type="button"
                            disabled={assigning || quota?.canAssignMore === false}
                            onClick={async () => {
                              setAssigning(true);
                              try {
                                await csrOutreachService.assign([c.userId]);
                                toast.success("Assigned");
                                refresh();
                              } catch (e) {
                                toast.error(e?.response?.data?.message || "Failed");
                              } finally {
                                setAssigning(false);
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                            style={{ backgroundColor: THEME.primary }}
                          >
                            <FiUserPlus /> Assign
                          </button>
                        ) : canManageCsrCustomers ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setChatCustomer(c)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700"
                            >
                              <FiMessageCircle /> Contact
                            </button>
                            <button
                              type="button"
                              onClick={() => openLogCallModal(c)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700"
                            >
                              <FiClock /> Log time
                            </button>
                            {(isLead || isAdmin) && tab === "team" && c.assignmentId && (
                              <>
                                <select
                                  className="border border-gray-200 rounded-lg px-1.5 py-1 text-[11px] max-w-[130px] disabled:opacity-50"
                                  defaultValue=""
                                  disabled={reassigningId === c.assignmentId || unassigningId === c.assignmentId}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    e.target.value = "";
                                    if (v) handleReassign(c.assignmentId, v);
                                  }}
                                >
                                  <option value="">
                                    {reassigningId === c.assignmentId ? "Shifting…" : "Shift to…"}
                                  </option>
                                  {teamAgents
                                    .filter((a) => Number(a.id) !== Number(c.agentId))
                                    .map((a) => (
                                      <option key={a.id} value={a.id}>
                                        {a.name}
                                      </option>
                                    ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => handleRelease(c.assignmentId)}
                                  disabled={reassigningId === c.assignmentId || unassigningId === c.assignmentId}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-800 disabled:opacity-50"
                                >
                                  {unassigningId === c.assignmentId ? (
                                    <>
                                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-amber-700 border-t-transparent" />
                                      Unassigning…
                                    </>
                                  ) : (
                                    "Unassign"
                                  )}
                                </button>
                                {reassigningId === c.assignmentId && (
                                  <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                                    <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                                    Shifting…
                                  </span>
                                )}
                              </>
                            )}
                            {isDeveloper && (
                              <button
                                type="button"
                                onClick={() => requestDeleteCustomer(c)}
                                disabled={deletingCallId === `customer-${c.assignmentId}`}
                                className="inline-flex items-center justify-center p-2 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 disabled:opacity-40"
                                title="Delete customer & all related data (Developer only)"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic">View only</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!list.length && (
                  <tr>
                    <td colSpan={8} className="px-3 py-12 text-center text-gray-400">
                      No users in this view
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {isAdmin && tab === "performance" && (
          <p className="text-xs text-gray-400 mt-4 flex items-center gap-1">
            <FiTrendingUp /> Admin overview — all CSR agents. Chat Support agents work in Support Center.
          </p>
        )}
      </div>

      {chatCustomer && (
        <CsrCustomerChat
          customer={chatCustomer}
          onClose={() => setChatCustomer(null)}
          onCallEnded={refresh}
        />
      )}

      {historyModal && (
        <CsrCallHistoryModal
          customer={historyModal}
          onClose={() => setHistoryModal(null)}
          onUpdated={refresh}
          canDelete={isDeveloper}
          onDelete={requestDeleteCallRecord}
          deletingId={deletingCallId}
          dataVersion={dataVersion}
        />
      )}

      <ConfirmWarningModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        variant={confirmModal?.variant || "danger"}
        confirming={Boolean(deletingCallId)}
        onClose={() => {
          if (!deletingCallId) setConfirmModal(null);
        }}
        onConfirm={() => confirmModal?.onConfirm?.()}
      />

      {premiumModalCustomer && (
        <CsrPremiumProofModal
          customer={premiumModalCustomer}
          onClose={() => setPremiumModalCustomer(null)}
          onSubmitted={refresh}
        />
      )}

      {approvalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
            <h3 className="text-lg font-bold text-gray-900">
              {approvalModal.status === "approved" ? "Accept payment proof?" : "Reject payment proof?"}
            </h3>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              {approvalModal.status === "approved"
                ? `Approve proof for ${approvalModal.label} and grant premium.`
                : `Reject proof for ${approvalModal.label}. CSR can resubmit.`}
            </p>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes (optional)</label>
            <textarea
              value={approvalModal.notes}
              onChange={(e) => setApprovalModal((m) => ({ ...m, notes: e.target.value }))}
              className={`${inputCls} w-full min-h-[70px] mb-4`}
              placeholder="Reason or verification notes…"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setApprovalModal(null)}
                disabled={!!approvingId}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePremiumApproval}
                disabled={!!approvingId}
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 ${
                  approvalModal.status === "approved" ? "bg-emerald-600" : "bg-rose-600"
                }`}
              >
                {approvingId
                  ? "Saving…"
                  : approvalModal.status === "approved"
                    ? "Accept & grant premium"
                    : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {callModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h3 className="font-bold text-gray-900 mb-1">Log call record</h3>
            <p className="text-xs text-gray-500 mb-4">
              Add a manual call entry. Each save creates a separate row in call history.
            </p>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Client email</label>
            <input
              type="email"
              readOnly
              value={callModal.email}
              className={`${inputCls} w-full mb-3 bg-gray-50`}
            />
            <label className="block text-xs font-semibold text-gray-500 mb-1">Call date & time</label>
            <input
              type="datetime-local"
              value={callModal.started_at}
              onChange={(e) => setCallModal((m) => ({ ...m, started_at: e.target.value }))}
              className={`${inputCls} w-full mb-3`}
            />
            <label className="block text-xs font-semibold text-gray-500 mb-1">Duration</label>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <span className="text-[10px] text-gray-400">Hours</span>
                <input
                  type="number"
                  min={0}
                  value={callModal.hours}
                  onChange={(e) => setCallModal((m) => ({ ...m, hours: e.target.value }))}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <span className="text-[10px] text-gray-400">Minutes</span>
                <input
                  type="number"
                  min={0}
                  value={callModal.minutes}
                  onChange={(e) => setCallModal((m) => ({ ...m, minutes: e.target.value }))}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <span className="text-[10px] text-gray-400">Seconds</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={callModal.seconds}
                  onChange={(e) => setCallModal((m) => ({ ...m, seconds: e.target.value }))}
                  className={`${inputCls} w-full`}
                />
              </div>
            </div>
            <textarea
              placeholder="Notes (optional)"
              value={callModal.notes}
              onChange={(e) => setCallModal((m) => ({ ...m, notes: e.target.value }))}
              className={`${inputCls} w-full mb-4 min-h-[60px]`}
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setCallModal(null)}
                disabled={loggingCall}
                className="px-4 py-2 text-sm rounded-lg border"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogMinutes}
                disabled={loggingCall}
                className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {loggingCall ? "Saving…" : "Save record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddByPhone && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <FiPhone style={{ color: THEME.primary }} /> Add lead by phone
              </h3>
              <button
                type="button"
                onClick={() => setShowAddByPhone(false)}
                className="text-gray-400 hover:text-gray-700"
              >
                <FiXCircle />
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Phone is required. If the user exists and is already assigned to another CSR, you cannot claim them — only a Team Lead can unassign or shift.
            </p>
            <input
              className={inputCls + " w-full"}
              placeholder="Phone number"
              value={phoneLead}
              onChange={(e) => setPhoneLead(e.target.value)}
            />
            {phoneChecking ? (
              <p className="text-[11px] text-gray-400">Checking phone…</p>
            ) : phoneLookupError ? (
              <p className="text-[11px] text-rose-600">{phoneLookupError}</p>
            ) : phoneLookup?.userExists ? (
              <p className="text-[11px] text-emerald-700">
                User found: {phoneLookup.matchedUser?.name || phoneLookup.matchedUser?.email}
                {phoneLookup.matchedUser?.contactNo ? ` · ${phoneLookup.matchedUser.contactNo}` : ""}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddByPhone(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={phoneAssigning || phoneChecking || Boolean(phoneLookupError) || !phoneLead.trim()}
                onClick={handleAssignByPhone}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {phoneAssigning ? "Assigning…" : "Assign to me"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
